import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { SlSelectEvent } from '@shoelace-style/shoelace';
import { api } from '../api.js';
import { getInitials } from '../utils.js';

/**
 * A row of the project's access list: the collaborator and a control to change
 * or remove their role.
 */
@customElement('project-collaborator')
export class ProjectCollaborator extends LitElement {

  @property({ attribute: false }) project?: WireTypes.Project;
  @property({ attribute: false }) user?: WireTypes.User;
  @property({ attribute: false }) projectRole?: WireTypes.ProjectRole;

  override render() {
    const user = this.user;
    const project = this.project;
    const projectRole = this.projectRole;

    if (!user || !project)
      return nothing;

    const disabled = project.access !== 'editor';
    return html`
      <sl-avatar image=${user.avatarUrl} initials=${getInitials(user.userName)}></sl-avatar>
      <div class=lines>
        <div class=primary>${user.userLogin}</div>
        ${user.userName !== user.userLogin ? html`<div class=secondary>${user.userName}</div>` : nothing}
      </div>
      <sl-dropdown placement=bottom-end>
        <sl-button size=small ?disabled=${disabled} slot=trigger caret>${roleLabel(projectRole)}</sl-button>
        <sl-menu @sl-select=${async (event: SlSelectEvent) => {
          let newRole: 'editor'|'viewer' | undefined;
          if (event.detail.item.value === 'remove-access')
            newRole = undefined;
          else if (event.detail.item.value === 'make-viewer')
            newRole = 'viewer';
          else if (event.detail.item.value === 'make-editor')
            newRole = 'editor';
          await api.project.shareProject.POST({
            orgSlug: project.org.orgSlug,
            projectSlug: project.projectSlug,
            userId: user.userId,
            access: newRole,
          });
          this.projectRole = newRole;
          if (!newRole)
            this.dispatchEvent(new Event('collaborator-removed', { bubbles: true, composed: true }));
        }}>
          <sl-menu-item value='make-viewer' type=checkbox ?checked=${projectRole === 'viewer'}>Viewer</sl-menu-item>
          <sl-menu-item value='make-editor' type=checkbox ?checked=${projectRole === 'editor'}>Editor</sl-menu-item>
          ${projectRole ? html`
            <sl-divider></sl-divider>
            <sl-menu-item value='remove-access'>Remove access</sl-menu-item>
          ` : nothing}
        </sl-menu>
      </sl-dropdown>
    `;
  }

  static styles = [css`
    :host {
      display: grid;
      grid-template-columns: auto 1fr auto;
      align-items: center;
      gap: var(--sl-spacing-small);
    }

    sl-avatar {
      --size: 2rem;
    }

    .secondary {
      color: var(--sl-color-neutral-500);
      font-size: var(--sl-font-size-small);
    }
  `]
}

function roleLabel(role: WireTypes.ProjectRole|undefined): string {
  if (role === 'editor')
    return 'Editor';
  if (role === 'viewer')
    return 'Viewer';
  return 'No access';
}
